import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneStartDecision } from '../types';
import MilestoneStartSelector from './MilestoneStartSelector';
import Modal from './Modal';
import { saveErrorText } from '../utils/apiError';

interface MilestoneStartModalProps {
  milestones: Milestone[];
  userRole: 'company' | 'person';
  onSave: (decisions: MilestoneStartDecision[]) => Promise<void>;
  onClose: () => void;
}

export default function MilestoneStartModal({
  milestones,
  userRole,
  onSave,
  onClose
}: MilestoneStartModalProps) {
  const { t } = useTranslation();
  const [decisions, setDecisions] = useState<MilestoneStartDecision[] | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const single = milestones.length === 1 ? milestones[0] : null;
  const canConfirmStart = single &&
    (single.itemType ?? 'milestone') === 'milestone' &&
    !single.workStartedAt && (!single.deliveryStatus || single.deliveryStatus === 'not_started') &&
    !(single.companyAttestation === 'approved' && single.personAttestation === 'approved') &&
    (userRole === 'company' || !single.companyStartDecision);
  // The stage was already chosen in the timeline. Only this explicit
  // confirmation saves its start; opening/cancelling the dialog does nothing.
  const decisionsToSave = canConfirmStart
    ? [{ milestoneId: single.id, started: true }]
    : decisions;

  const save = async () => {
    if (saving || !decisionsToSave?.length) return;
    setSaving(true);
    setError('');
    try {
      await onSave(decisionsToSave);
      onClose();
    } catch (saveError) {
      // The choice stays, so the user can try again (ClickUp 869fgxq45).
      setError(saveErrorText(t, saveError, 'milestoneStarts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      zIndex={1060}
      onClose={() => { if (!saving) onClose(); }}
      title={(
        <>
          <i className="feather-play-circle me-2" aria-hidden="true"></i>
          {single ? t('milestoneStarts.modalSingleTitle') : t('milestoneStarts.modalTitle')}
        </>
      )}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving || !decisionsToSave?.length}
          >
            <i className="feather-check me-1" aria-hidden="true"></i>
            {saving ? t('milestoneStarts.saving')
              : canConfirmStart ? t('milestoneStarts.confirmStart')
                : single ? t('milestoneStarts.saveSingle') : t('milestoneStarts.save')}
          </button>
        </>
      )}
    >
      {canConfirmStart ? (
        <div data-testid="milestone-start-confirmation">
          <p className="fw-semibold">{t('milestoneStarts.confirmStartQuestion', { number: single.milestoneNumber })}</p>
          <p>{single.description}</p>
          <p className="small text-muted mb-0">{t('milestoneStarts.selectorManualHint')}</p>
        </div>
      ) : (
        <MilestoneStartSelector
          milestones={milestones}
          userRole={userRole}
          context="manual"
          disabled={saving}
          onDecisionsChange={setDecisions}
        />
      )}
      {error && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
